import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';

function basename(path) {
    return (path ?? '').split('\\').pop().split('/').pop();
}

export default function VisualizarArquivoModal({ show, onHide, arquivoId, nome, localFile, url }) {
    const [contentType, setContentType] = useState(null);
    const [textContent, setTextContent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    // A imagem pelo link (a do ObservacaoAnexos) pode ainda estar chegando: até o navegador tê-la, o Carregando.
    const [imagemPronta, setImagemPronta] = useState(true);

    // `url`: o endereço do arquivo, para quem já tem o link e não o id (o anexo gravado do ObservacaoAnexos).
    const serverUrl = url || (arquivoId
        ? `${process.env.REACT_APP_API_URL}/arquivo/download/${arquivoId}`
        : null);

    const filename = basename(nome) || localFile?.name || '';

    useEffect(() => {
        if (!show) return;

        let blobUrl = null;
        const controller = new AbortController();

        setLoading(true);
        setContentType(null);
        setTextContent(null);
        setPreviewUrl(null);
        setImagemPronta(true);

        if (localFile) {
            blobUrl = URL.createObjectURL(localFile);
            setPreviewUrl(blobUrl);
            const ct = localFile.type || 'application/octet-stream';
            setContentType(ct);
            if (ct.startsWith('text/')) {
                const reader = new FileReader();
                reader.onload = e => { if (!controller.signal.aborted) setTextContent(e.target.result); };
                reader.readAsText(localFile);
            }
            setLoading(false);
            return () => { controller.abort(); URL.revokeObjectURL(blobUrl); };
        }

        if (!serverUrl) { setLoading(false); return; }

        // A imagem que já vem com o link (a do ObservacaoAnexos) aparece pelo mesmo endereço, sem baixar de novo: o
        // navegador reaproveita a que o campo já carregou.
        const extensaoImagem = url ? (filename.match(/\.(png|jpe?g|gif|webp|bmp|svg)$/i) || [])[1] : null;
        if (extensaoImagem) {
            const extensao = extensaoImagem.toLowerCase();
            setContentType(`image/${{ jpg: 'jpeg', svg: 'svg+xml' }[extensao] || extensao}`);
            setPreviewUrl(url);
            setImagemPronta(false);
            setLoading(false);
            return undefined;
        }

        fetch(serverUrl, { signal: controller.signal })
            .then(async r => {
                const ct = r.headers.get('content-type')?.split(';')[0].trim() ?? 'application/octet-stream';
                setContentType(ct);

                if (ct.startsWith('video/')) {
                    setPreviewUrl(serverUrl);
                    return;
                }

                const blob = await r.blob();
                if (controller.signal.aborted) return;
                blobUrl = URL.createObjectURL(blob);
                setPreviewUrl(blobUrl);

                if (ct.startsWith('text/')) {
                    setTextContent(await blob.text());
                }
            })
            .catch(err => {
                if (err.name === 'AbortError') return;
                setContentType('application/octet-stream');
                setPreviewUrl(serverUrl);
            })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });

        return () => {
            controller.abort();
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [show, localFile, serverUrl, url, filename]);

    const isImage = contentType?.startsWith('image/');
    const isPdf = contentType === 'application/pdf';
    const isVideo = contentType?.startsWith('video/');
    const isText = contentType?.startsWith('text/');
    const noPadding = isPdf || isVideo;

    function renderContent() {
        if (loading || !contentType) {
            return <div className="text-center py-5 text-muted">Carregando...</div>;
        }
        if (isImage) {
            // A que o navegador já tem aparece na hora (o `complete` do ref, antes da pintura); a que ainda está
            // chegando fica escondida, com o Carregando, até o load.
            return (
                <div className="text-center">
                    {!imagemPronta && <div className="py-5 text-muted">Carregando...</div>}
                    <img
                        src={previewUrl}
                        alt={filename}
                        ref={(el) => { if (el && !imagemPronta && el.complete && el.naturalWidth) setImagemPronta(true); }}
                        onLoad={() => setImagemPronta(true)}
                        onError={() => setImagemPronta(true)}
                        style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', display: imagemPronta ? undefined : 'none' }}
                    />
                </div>
            );
        }
        if (isPdf) {
            return (
                <iframe
                    src={previewUrl}
                    title={filename}
                    style={{ width: '100%', height: '75vh', border: 'none', display: 'block' }}
                />
            );
        }
        if (isVideo) {
            return (
                <video controls style={{ width: '100%', display: 'block', background: '#000' }}>
                    <source src={previewUrl} type={contentType} />
                    Seu navegador não suporta reprodução de vídeo.
                </video>
            );
        }
        if (isText) {
            return (
                <pre style={{ maxHeight: '70vh', overflow: 'auto', fontSize: 13, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {textContent ?? 'Carregando conteúdo...'}
                </pre>
            );
        }
        return (
            <div className="text-center py-5">
                <p className="text-muted mb-0">Visualização não disponível para este tipo de arquivo.</p>
            </div>
        );
    }

    return (
        <Modal size="xl" show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title className="text-break">
                    {filename}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: noPadding ? 0 : undefined }}>
                {renderContent()}
            </Modal.Body>
            {previewUrl && (
                <Modal.Footer>
                    <a href={previewUrl} download={filename} className="btn btn-outline-primary btn-sm">
                        Baixar
                    </a>
                </Modal.Footer>
            )}
        </Modal>
    );
}
